import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, catchError, debounceTime } from 'rxjs/operators';
import { asapScheduler, EMPTY, of, scheduled } from 'rxjs';
import { HomesService } from './homes.service';
import {
  EnumHomesActions,
  GetHomes,
  GetHomesSuccess,
  GetHomesFailure,
  UpdateHomePlace,
  UpdateHomePlaceSuccess,
  UpdateHomePlaceFailure,
  GetHomeStatus,
  GetHomeStatusFailure,
  GetHomeConfig,
  GetHomeConfigFailure,
  SetConfig,
  SetConfigFailure,
  ShowSpinner,
  HideSpinner,
  GetHomeUsers,
  GetHomeUsersSuccess,
  GetHomeUsersFailure,
  ShowErrorModal,
  RemoveUserFromHome,
  RemoveUserFromHomeSuccess,
  RemoveUserFromHomeFailure,
  GetAdminAccessCode,
  GetAdminAccessCodeSuccess,
  GetAdminAccessCodeFailure,
  GetHomeConfigSuccess,
  SetConfigSuccess,
  GetHomeStatusSuccess,
  SetState,
  UpdateHomeName,
  UpdateHomeNameSuccess,
  UpdateHomeNameFailure,
  SetTemperatureControlModeSuccess,
  SetTemperatureControlModeFailure,
  SetTemperatureControlMode,
  GetHomeNFASuccess,
  GetHomeNFAFailure,
  GetHomeNFA,
  SetStateSuccess,
  SetStateErrors,
  SetStateFailure,
  SetHomeType,
  SetHomeTypeSuccess,
  SetHomeTypeFailure,
  RemoveDeviceFromHome,
  RemoveDeviceFromHomeSuccess,
  RemoveDeviceFromHomeFailure,
  GetReadonlyDeviceStateSuccess,
  GetReadonlyDeviceStateFailure,
  GetReadonlyDeviceState,
  SelectHome,
  SetThermMode,
  SetThermModeSuccess,
  SetThermModeFailure,
  SetThermPointSuccess,
  SetThermPointFailure,
  SetThermPoint,
  SetHeatingMode,
  SetHeatingModeSuccess,
  SetHeatingModeFailure,
  SetCoolingMode,
  SetCoolingModeSuccess,
  SetCoolingModeFailure,
  SetAutoMode,
  SetAutoModeSuccess,
  SetAutoModeFailure
} from './homes.action';

import { HomesData, HomeSyncPayload, Place } from './homes.interface';
import { SetHomeUser } from '../user/user.action';
import { Store } from '@ngrx/store';
import { SettingsState } from '@library/utils/interfaces/settings-state.interface';
import { SetModulesErrors, SetModulesStatusErrors, UpdateModulesSetConfig, UpdateModulesStatus } from '../modules/modules.action';
import { UpdateRoomsSetConfig, UpdateRoomsStatus } from '../rooms/rooms.action';

@Injectable()
export class HomesEffects {
  constructor(
    private homesService: HomesService,
    private actions$: Actions,
    private store: Store<SettingsState>,
  ) { }

  getHomes$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomes>(EnumHomesActions.GetHomes),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.getHomes(action.payload).pipe(
        switchMap((homes: HomesData) => [
          new HideSpinner(),
          new GetHomesSuccess(homes.homes, homes.modules),
          SetHomeUser({user: homes.user}),
        ]),
        catchError(({ error }) => of(new GetHomesFailure(error), new HideSpinner())),
      );
    })
  ));

  // @deprecated
  // Select home if only one in hoesdata
  // This should be deleted because it can cause race conditions and is useless
  selectHome$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomesSuccess>(EnumHomesActions.GetHomesSuccess),
    debounceTime(0), // Needed to cancel the race condition
    switchMap((action) => {
      if (action.payload.length === 1) {
        return [new SelectHome(action.payload[0].id)];
      } else {
        return EMPTY;
      }
    }),
  ));

  updateHomePlace$ = createEffect(() => this.actions$.pipe(
    ofType<UpdateHomePlace>(EnumHomesActions.UpdateHomePlace),
    switchMap((action: UpdateHomePlace) => {
      return this.homesService.updateHomePlace(action.payload).pipe(
        switchMap((updatedHome: Place) => [
          new UpdateHomePlaceSuccess({ updatedHome, home_id: action.payload.home_id }),
        ]),
        catchError(({ error }) => of(new UpdateHomePlaceFailure(error))),
      );
    })
  ));

  getHomesStatus$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomeStatus>(EnumHomesActions.GetHomeStatus),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.getHomeStatus(action.payload).pipe(
        switchMap(({home, errors}) => [
          new HideSpinner(),
          new SetModulesStatusErrors({ id: action.payload.home_id, errors: errors || [] }),
          new GetHomeStatusSuccess(home || { id: action.payload.home_id, rooms: [], modules: []}),
        ]),
        catchError(({ error }) => of(new GetHomeStatusFailure(error), new HideSpinner())),
      );
    })
  ));

  getReadonlyDeviceState$ = createEffect(() => this.actions$.pipe(
    ofType<GetReadonlyDeviceState>(EnumHomesActions.GetReadonlyDeviceState),
    switchMap(() => {
      return this.homesService.getReadonlyDeviceState().pipe(
        switchMap(({modules, errors}) => [
          new GetReadonlyDeviceStateSuccess(modules ?? [], errors ?? []),
        ]),
        catchError(({ error }) => of(new GetReadonlyDeviceStateFailure(error))),
      );
    })
  ));

  getHomesConfig$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomeConfig>(EnumHomesActions.GetHomeConfig),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.getHomeConfig(action.payload).pipe(
        switchMap(({home, errors}) => [
          new HideSpinner(),
          new SetModulesErrors({ id: action.payload.home_id, errors: errors || [] }),
          new GetHomeConfigSuccess(home || { id: action.payload.home_id, modules: [], rooms: [] })
        ]),
        catchError((error) => {
          return of(new GetHomeConfigFailure(error), new HideSpinner(), new ShowErrorModal());
        }),
      );
    })
  ));

  setHomesConfig$ = createEffect(() => this.actions$.pipe(
    ofType<SetConfig>(EnumHomesActions.SetConfig),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.setHomeConfig(action.payload).pipe(
        switchMap(({errors}) => [
            new SetModulesErrors({id: action.payload.home_id, errors: errors || []}),
            new UpdateModulesSetConfig(action.payload),
            new UpdateRoomsSetConfig(action.payload),
            new HideSpinner(),
            new SetConfigSuccess()
        ]),
        catchError(({ error }) => of(new SetConfigFailure(error), new HideSpinner(), new ShowErrorModal())),
      );
    })
  ));

  getHomeUsers$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomeUsers>(EnumHomesActions.GetHomeUsers),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.getHomeUsers(action.payload).pipe(
        switchMap((users) => [
          new GetHomeUsersSuccess({ id: action.payload, users }),
          new HideSpinner(),
        ]),
        catchError(({ error }) => of(new HideSpinner(), new ShowErrorModal(), new GetHomeUsersFailure(error))),
      );
    })
  ));

  removeUserFromHome$ = createEffect(() => this.actions$.pipe(
    ofType<RemoveUserFromHome>(EnumHomesActions.RemoveUserFromHome),
    switchMap((action) => {
      return this.homesService.removeUserAccessToHome(action.payload.homeId, action.payload.userId).pipe(
        switchMap(() => [
          new RemoveUserFromHomeSuccess({ homeId: action.payload.homeId, userId: action.payload.userId})
        ]),
        catchError(({ error }) => of(new ShowErrorModal(), new RemoveUserFromHomeFailure(error))),
      );
    })
  ));

  updateHomeName$ = createEffect(() => this.actions$.pipe(
      ofType<UpdateHomeName>(EnumHomesActions.UpdateHomeName),
      switchMap( (action) => {
        return this.homesService.updateHome(action.payload).pipe(
          switchMap(() => [
            new UpdateHomeNameSuccess(action.payload)
          ]),
          catchError( ({ error }) => of(new UpdateHomeNameFailure(error), new ShowErrorModal()))
        );
      })
  ));

  getAdminAccessCode$ = createEffect(() => this.actions$.pipe(
    ofType<GetAdminAccessCode>(EnumHomesActions.GetAdminAccessCode),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.generateAdminAccessCode(action.payload).pipe(
        switchMap((res: {admin_access_code: string}) => [
          new GetAdminAccessCodeSuccess({ homeId: action.payload, accessCode: res.admin_access_code }),
          new HideSpinner(),
        ]),
        catchError(({ error }) => of(new HideSpinner(), new ShowErrorModal(), new GetAdminAccessCodeFailure(error))),
      );
    })
  ));

  setState$ = createEffect(() => this.actions$.pipe(
    ofType<SetState>(EnumHomesActions.SetState),
    switchMap((action) => {
      this.store.dispatch(new ShowSpinner());
      return this.homesService.setState(action.payload, action.header).pipe(
        switchMap((payload) => {
          if (payload.errors) {
            return [
            new SetStateErrors({errors: payload.errors}),
            new HideSpinner(),
          ]} else {
            const returnPayload = action.payload.home as HomeSyncPayload;
            return [
              new SetStateSuccess(),
              new UpdateModulesStatus(returnPayload),
              new UpdateRoomsStatus(returnPayload),
              new HideSpinner(),
            ]
          }
      }),

        catchError(({ error }) => of(new SetStateFailure(error), new HideSpinner(), new ShowErrorModal())),
      );
    })
  ));

  setTemperatureControlMode$ = createEffect(() => this.actions$.pipe(
    ofType<SetTemperatureControlMode>(EnumHomesActions.SetTemperatureControlMode),
    switchMap((action) => {
      return this.homesService.setHomeData(action.payload).pipe(
        switchMap(() => [
          new SetTemperatureControlModeSuccess(action.payload)
        ]),
        catchError(({ error }) =>
          of(new SetTemperatureControlModeFailure(error))
        )
      );
    })
  ));

  getHomeNFA$ = createEffect(() => this.actions$.pipe(
    ofType<GetHomeNFA>(EnumHomesActions.GetHomeNFA),
    switchMap( (action) => {
      return this.homesService.getHomeNFA(action.payload).pipe(
        switchMap((res) => [
          new GetHomeNFASuccess(res)
        ]),
        catchError( ({error}) =>
          of(new GetHomeNFAFailure(error))
        )
      );
    })
  ));

  setHomeType$ = createEffect(() => this.actions$.pipe(
    ofType(EnumHomesActions.SetHomeType),
    switchMap((action: SetHomeType) => {
      return this.homesService.setHomeData(action.payload).pipe(
        switchMap(() => [
            new SetHomeTypeSuccess(action.payload)
        ]),
        catchError(({ error }) =>
          scheduled(of(new SetHomeTypeFailure(error)), asapScheduler))
        );
      })
    )
  );

  removeDeviceFromHome$ = createEffect(() => this.actions$.pipe(
    ofType<RemoveDeviceFromHome>(EnumHomesActions.RemoveDeviceFromHome),
    switchMap( (action) => {
      return this.homesService.removeDeviceFromHome(action.payload).pipe(
        switchMap((res) => [
          new RemoveDeviceFromHomeSuccess(res)
        ]),
        catchError( ({error}) =>
          of(new RemoveDeviceFromHomeFailure(error))
        )
      );
    })
  ));

  setThermMode$ = createEffect(() => this.actions$.pipe(
    ofType<SetThermMode>(EnumHomesActions.SetThermMode),
    switchMap( (action) => {
      return this.homesService.setThermMode(action.homeId, action.therm_mode, action.endtime).pipe(
        switchMap((res) => [
          new SetThermModeSuccess(action.homeId, action.therm_mode, action.endtime)
        ]),
        catchError( ({error}) =>
          of(new SetThermModeFailure(error))
        )
      );
    })
  ));

  setThermPoint$ = createEffect(() => this.actions$.pipe(
    ofType<SetThermPoint>(EnumHomesActions.SetThermPoint),
    switchMap( (action) => {
      return this.homesService.setThermPoint(action.params).pipe(
        switchMap((res) => [
          new SetThermPointSuccess(action.params)
        ]),
        catchError( ({error}) =>
          of(new SetThermPointFailure(error))
        )
      );
    })
  ));


  setHeatingModeMode$ = createEffect(() => this.actions$.pipe(
    ofType<SetHeatingMode>(EnumHomesActions.SetHeatingMode),
    switchMap((action) => {
      return this.homesService.setHomeData(action.payload).pipe(
        switchMap(() => [
          new SetHeatingModeSuccess(action.payload)
        ]),
        catchError(({ error }) =>
          of(new SetHeatingModeFailure(error))
        )
      );
    })
  ));

  setCoolingMode$ = createEffect(() => this.actions$.pipe(
    ofType<SetCoolingMode>(EnumHomesActions.SetCoolingMode),
    switchMap((action) => {
      return this.homesService.setHomeData(action.payload).pipe(
        switchMap(() => [
          new SetCoolingModeSuccess(action.payload)
        ]),
        catchError(({ error }) =>
          of(new SetCoolingModeFailure(error))
        )
      );
    })
  ));

  setAutoMode$ = createEffect(() => this.actions$.pipe(
    ofType<SetAutoMode>(EnumHomesActions.SetAutoMode),
    switchMap((action) => {
      return this.homesService.setHomeData(action.payload).pipe(
        switchMap(() => [
          new SetAutoModeSuccess(action.payload)
        ]),
        catchError(({ error }) =>
          of(new SetAutoModeFailure(error))
        )
      );
    })
  ));
}


